import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { IAthlete, IAthleteCSVRow, IGroup, ITeam } from '../../../../../data/types';
import { useListSearch } from '../../../../../hooks/useListSearch';
import { FullHeightColumn } from '../../../../layout/Column';
import { Row } from '../../../../layout/Row';
import { ScrollableArea } from '../../../../layout/ScrollableArea';
import { Count } from '../../../../shared/Count';
import { ListControlContainer } from '../../../../shared/List/ListControlContainer';
import { ListControlLabel } from '../../../../shared/form/ListControlLabel';
import { MultiGroupSelectList } from '../../groups/MultiGroupSelectList';
import { MultiTeamSelectList } from '../../teams/MultiTeamSelectList';
import { IErrorState } from '../validation';
import { CSVImport } from './CSVImport';
import { ImportList } from './ImportList';

interface IProps {
  importList: Array<Partial<IAthlete>>;
  onAthletesAdded: (athletes: IAthleteCSVRow[]) => void;
  onImportListUpdated: (athletes: Array<Partial<IAthlete>>) => void;
  teams: ITeam[];
  selectedTeams: ITeam[];
  teamSelectionHandler: (team: ITeam, checked: boolean) => void;
  groups: IGroup[];
  selectedGroups: IGroup[];
  onError: (error: IErrorState) => void;
  groupSelectionHandler: (group: IGroup, check: boolean) => void;
  errorItems?: Array<Partial<IAthlete>>;
}

const Import = (props: IProps): React.ReactElement => {
  /**
   * @description removes an imported athlete from staging list
   * @param index
   */
  const removeItem = (index: number): void => {
    const list = cloneDeep(props.importList);
    list.splice(index, 1);
    props.onImportListUpdated(list);
  };

  const {
    list: groups,
    search: searchGroups,
    clear: clearGroupSearch,
    searchTerm: groupSearchTerm,
  } = useListSearch(props.groups);

  const {
    list: teams,
    search: searchTeams,
    clear: clearTeamsSearch,
    searchTerm: teamSearchTerm,
  } = useListSearch(props.teams);

  return (
    <Row shouldCollapse>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Team(s)'
          helpText='An athlete must be assigned to at least one team'
          onSearchClearHandler={clearTeamsSearch}
          searchValue={teamSearchTerm}
          onSearchHandler={searchTeams}
        >
          <Count value={props.selectedTeams.length} />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea>
            <MultiTeamSelectList
              teams={teams}
              selectedTeams={props.selectedTeams}
              onSelect={props.teamSelectionHandler}
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Group(s)'
          onSearchClearHandler={clearGroupSearch}
          searchValue={groupSearchTerm}
          onSearchHandler={searchGroups}
        >
          <Count value={props.selectedGroups.length} />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiGroupSelectList
              teams={props.teams}
              selectedTeams={props.selectedTeams}
              disabled={props.selectedTeams.length < 1}
              groups={groups}
              selectedGroups={props.selectedGroups}
              onSelect={props.groupSelectionHandler}
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
      <FullHeightColumn>
        <CSVImport
          onError={props.onError}
          handleImport={props.onAthletesAdded}
          selectedTeams={props.selectedTeams}
        />
        <ImportList
          errorItems={props.errorItems}
          importList={props.importList}
          removeItem={removeItem}
          selectedTeams={props.selectedTeams}
        />
      </FullHeightColumn>
    </Row>
  );
};

export { Import };
